import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Card, CardContent, CardMedia, Box, Typography, Button, Stack, Divider, TextField, CircularProgress, Container } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import Page from './Page';
import Header from './Header';
import { useCart } from './contexts/CartContext.jsx';
import { getPrimaryImageUrl } from './utils/productImage';

const formatPrice = cents => `$${((cents || 0) / 100).toFixed(2)}`;

const formatDate = date => {
  if (!date) return 'TBD';
  return new Date(date).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const Cart = () => {
  const navigate = useNavigate();
  const { cart, loading, removeItem } = useCart();
  const items = cart?.items || [];
  const subtotal = cart?.totals?.subtotal_cents || 0;

  return (
    <Page>
      <Container maxWidth="lg">
        <Header text="Cart" />
        {loading && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
            <CircularProgress size={24} />
            <Typography variant="body1">Loading your cart…</Typography>
          </Stack>
        )}
        <Grid container spacing={2}>
	<Grid item xs={12} md={8}>
	  {!loading && items.length === 0 && (
	    <Typography variant="body1">Your cart is empty. Head to the catalog to add a cloth.</Typography>
	  )}
	  {items.map(item => (
	    <Card key={item.id} sx={{ display: 'flex', flexDirection: 'row', mb: 2 }}>
	      <Grid container spacing={1}>
	        <Grid item xs={12} md={4}>
		  <Box sx={{ paddingTop: 1, paddingLeft: 1 }}>
		    <CardMedia
		      component="img"
		      sx={{ width: '100%', objectFit: 'contain', backgroundColor: '#f9f9f9' }}
		      image={getPrimaryImageUrl(item.product)}
		      alt={item.product?.name}
		    >
		    </CardMedia>
		  </Box>
		</Grid>
		<Grid item xs={12} md={5}>
		  <CardContent>
		    <Typography variant="h6">{item.product?.name}</Typography>
		    <Divider />
                    <Typography variant="body2" sx={{ marginBottom: 1 }}>
                      Quantity: <strong>{item.quantity}</strong>
                    </Typography>
		    <Typography variant="body1">Start: {formatDate(item.rental_start_date)}</Typography>
		    <Typography variant="body1">Return: {formatDate(item.rental_end_date)}</Typography>
		  </CardContent>
		</Grid>
		<Grid item xs={12} md={3} sx={{ textAlign: 'left' }}>
		  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
		    <Button
		      color="primary"
		      startIcon={<DeleteIcon />}
		      onClick={() => removeItem(item.id)}
		      sx={{ ml: 2, textAlign: 'left', marginTop: 2 }}
		    >
		      Remove
		    </Button>
		  </Box>
		</Grid>
	      </Grid>
	    </Card>
	  ))}
	</Grid>
	  <Grid item xs={12} md={4}>
	  <Box
	    sx={{
	      border: '1px solid #ccc',
	      p: 2,
	      borderRadius: 1
	    }}
	  >
	    <Typography variant="h6" gutterBottom>
	      Cart Totals
	    </Typography>
	    <Box sx={{ my: 2 }}>
	      <Typography variant="body1" gutterBottom>
		Add a coupon
	      </Typography>
	      <TextField
		fullWidth
		placeholder="Enter coupon code"
		variant="outlined"
		size="small"
	      />
	    </Box>
	    <Divider />
	    <Stack spacing={1} sx={{ mt: 2 }}>
	      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
		<Typography>Subtotal</Typography>
		<Typography>{formatPrice(subtotal)}</Typography>
	      </Box>
	    </Stack>
	    <Divider sx={{ my: 2}} />
	    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
	      <Typography variant="h6">Total</Typography>
	      <Typography variant="h6">{formatPrice(subtotal)}</Typography>
	    </Box>
	    <Typography variant="body2" sx={{ mb: 2 }}>
	      Price includes prepaid return shipping and professional cleaning.
	    </Typography>
	    <Button
	      variant="contained"
	      color="primary"
	      fullWidth
	      onClick={() => navigate("/checkout")}
	      disabled={items.length === 0}
	    >
	      Proceed to Checkout
	    </Button>
            <Button
              variant="text"
              fullWidth
              sx={{ mt: 1 }}
              onClick={() => navigate('/products')}
            >
              Continue Shopping
            </Button>
	  </Box>
	</Grid>
        </Grid>
      </Container>
    </Page>
  );
}

export default Cart;
